/** Вход и «Первый запуск»: слева красная панель с логотипом, слоганом, ЭКГ и маскотом; справа форма. */
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router";
import { api, errText } from "../lib/api";
import { ME_KEY, useMe } from "../lib/auth";
import { EcgDraw, EcgLoader } from "../components/Ecg";
import { MASCOT } from "../components/ui";

export function LoginPage() {
  const { data: me, isLoading } = useMe();
  if (isLoading) {
    return (
      <div className="page-loading">
        <EcgLoader label="Открываю студию" />
      </div>
    );
  }
  if (me?.user) return <Navigate to="/write" replace />;
  return (
    <div className="login">
      <section className="login__brand" aria-hidden="true">
        <img className="login__logo" src="/brand/logo-white.png" alt="" width={320} height={128} />
        <p className="login__slogan">Практика — основа результатов!</p>
        <EcgDraw className="login__ecg" beats={3} />
        <img className="login__mascot" src={MASCOT.wave} alt="" width={260} height={345} />
      </section>
      <section className="login__form-wrap">{me?.needsSetup ? <SetupForm /> : <LoginForm />}</section>
    </div>
  );
}

function LoginForm() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/api/login", { login, password });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      nav("/write", { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="login__form" onSubmit={submit} noValidate>
      <h1>Вход</h1>
      <p className="muted">Контент-студия «Импульса»: посты ВКонтакте с Claude.</p>
      <label className="field">
        <span>Логин</span>
        <input className="input" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required autoFocus />
      </label>
      <label className="field">
        <span>Пароль</span>
        <input
          className="input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      {error && (
        <div className="error-line" role="alert">
          {error}
        </div>
      )}
      <button className="btn btn--primary btn--block" disabled={busy || !login || !password}>
        {busy ? "Вхожу…" : "Войти"}
      </button>
      <p className="small muted">Забыли пароль? На этом ПК выполните в папке платформы: npm run user:password -- логин новый-пароль</p>
    </form>
  );
}

function SetupForm() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [login, setLogin] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== repeat) return setError("Пароли не совпадают");
    if (password.length < 6) return setError("Пароль — не короче 6 символов");
    setBusy(true);
    setError("");
    try {
      await api.post("/api/setup", { login, name, password });
      await qc.invalidateQueries({ queryKey: ME_KEY });
      nav("/write", { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="login__form" onSubmit={submit} noValidate>
      <h1>Первый запуск</h1>
      <p className="muted">
        Придумайте логин и пароль. Платформой пользуетесь только вы, пароль защищает её, когда она переедет на сервер.
      </p>
      <label className="field">
        <span>Как к вам обращаться</span>
        <input
          className="input"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Константин"
          autoFocus
        />
      </label>
      <label className="field">
        <span>Логин</span>
        <input className="input" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required />
      </label>
      <div className="field">
        <label className="field__label" htmlFor="setup-password">
          Пароль
        </label>
        <input
          id="setup-password"
          className="input"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-describedby="setup-password-hint"
          required
        />
        <span id="setup-password-hint" className="field__hint">
          Не короче 6 символов
        </span>
      </div>
      <label className="field">
        <span>Пароль ещё раз</span>
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
          required
        />
      </label>
      {error && (
        <div className="error-line" role="alert">
          {error}
        </div>
      )}
      <button className="btn btn--primary btn--block" disabled={busy || !login || !password || !repeat}>
        {busy ? "Сохраняю…" : "Задать пароль и войти"}
      </button>
    </form>
  );
}
